import { useState, useEffect } from 'react';
import { createTicket } from '@/lib/api';
import { toast } from 'sonner';
import { X, AlertTriangle, GitBranch, Inbox, Bug, Shield } from 'lucide-react';

const TYPES = [
  { value: 'INCIDENT', label: 'Incident', icon: AlertTriangle, color: 'var(--nexus-danger)' },
  { value: 'CHANGE', label: 'Change', icon: GitBranch, color: 'var(--nexus-warning)' },
  { value: 'REQUEST', label: 'Request', icon: Inbox, color: 'var(--nexus-primary-bright)' },
  { value: 'PROBLEM', label: 'Problem', icon: Bug, color: 'var(--nexus-info)' },
  { value: 'SECURITY', label: 'Security', icon: Shield, color: 'var(--nexus-danger)' },
];

const PRIORITIES = [
  { value: 'P1', label: 'P1 - Critical', class: 'badge-p1' },
  { value: 'P2', label: 'P2 - High', class: 'badge-p2' },
  { value: 'P3', label: 'P3 - Medium', class: 'badge-p3' },
  { value: 'P4', label: 'P4 - Low', class: 'badge-p4' },
  { value: 'P5', label: 'P5 - Minimal', class: 'badge-p5' },
];

export const CreateTicketModal = ({ isOpen, onClose, defaultType = '' }) => {
  const [type, setType] = useState(defaultType || 'INCIDENT');
  const [priority, setPriority] = useState('P3');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);

  // Update type when defaultType changes
  useEffect(() => { if (defaultType) setType(defaultType); }, [defaultType]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setLoading(true);
    try {
      const ticket = await createTicket({ type, priority, title: title.trim(), description: description.trim() });
      toast.success(`Ticket ${ticket.id} created`, { description: `Assigned to ${ticket.assigned_agent}` });
      setTitle(''); setDescription(''); setType('INCIDENT'); setPriority('P3');
      onClose();
    } catch (err) {
      toast.error('Failed to create ticket', { description: err.response?.data?.detail || 'Unknown error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="create-ticket-modal">
      <div className="nexus-card relative w-full max-w-lg mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-[var(--nexus-border)]">
          <h2 className="text-sm font-semibold text-[var(--nexus-text-primary)]">Create New Ticket</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/[0.06]" data-testid="close-modal"><X size={16} className="text-[var(--nexus-text-muted)]" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Type selector */}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[var(--nexus-text-muted)] font-medium">Type</label>
            <div className="grid grid-cols-5 gap-2 mt-1">
              {TYPES.map(t => {
                const Icon = t.icon;
                return (
                  <button key={t.value} type="button" onClick={() => setType(t.value)}
                    className={`flex flex-col items-center gap-1 p-2 rounded-lg border transition-colors ${
                      type === t.value ? 'border-[var(--nexus-accent)] bg-[var(--nexus-accent)]/10' : 'border-[var(--nexus-border)] hover:bg-white/[0.03]'
                    }`} data-testid={`type-${t.value.toLowerCase()}`}>
                    <Icon size={14} style={{ color: type === t.value ? t.color : 'var(--nexus-text-muted)' }} />
                    <span className="text-[10px] text-[var(--nexus-text-secondary)]">{t.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
          {/* Priority */}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[var(--nexus-text-muted)] font-medium">Priority</label>
            <div className="flex gap-2 mt-1">
              {PRIORITIES.map(p => (
                <button key={p.value} type="button" onClick={() => setPriority(p.value)}
                  className={`flex-1 px-2 py-1.5 rounded-lg text-[10px] font-mono border transition-colors ${
                    priority === p.value ? `${p.class} font-bold` : 'border-[var(--nexus-border)] text-[var(--nexus-text-muted)] hover:bg-white/[0.03]'
                  }`} data-testid={`priority-${p.value.toLowerCase()}`}>{p.value}</button>
              ))}
            </div>
          </div>
          {/* Title */}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[var(--nexus-text-muted)] font-medium">Title</label>
            <input data-testid="ticket-title" type="text" value={title} onChange={e => setTitle(e.target.value)} required
              className="w-full mt-1 px-3 py-2 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)] text-sm text-[var(--nexus-text-primary)] placeholder:text-[var(--nexus-text-muted)] outline-none focus:border-[var(--nexus-accent)]" placeholder="Brief description of the issue" />
          </div>
          {/* Description */}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[var(--nexus-text-muted)] font-medium">Description</label>
            <textarea data-testid="ticket-description" value={description} onChange={e => setDescription(e.target.value)} rows={3}
              className="w-full mt-1 px-3 py-2 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)] text-sm text-[var(--nexus-text-primary)] placeholder:text-[var(--nexus-text-muted)] outline-none focus:border-[var(--nexus-accent)] resize-none" placeholder="Detailed description (optional)" />
          </div>
          <button data-testid="submit-ticket" type="submit" disabled={loading || !title.trim()}
            className="w-full py-2.5 rounded-lg bg-[var(--nexus-primary-bright)] text-white text-sm font-medium hover:bg-[var(--nexus-primary-bright)]/90 disabled:opacity-50 transition-colors">
            {loading ? 'Creating...' : 'Create Ticket'}
          </button>
        </form>
      </div>
    </div>
  );
};
